import { JobPartCategory } from "../../models/Job";
import {
  IPart,
  IPartAssembly,
  PartPosition,
  PartSide,
} from "../../models/Part";
import printHtml from "../../utils/printHtml";

export interface DismantlingPlanGroup {
  name: string;
  jobParts: JobPartCategory[];
}

export interface DismantlingPlanHeader {
  jobTitle?: string | null;
  vrm?: string | null;
  makeModel?: string | null;
}

const MAX_ORDER = Number.MAX_SAFE_INTEGER;

/**
 * Groups job parts under their assemblies, mirroring the ordering and matching
 * used by {@link JobPartsByAssembly}: assemblies in their stored order, each
 * with the job parts whose category belongs to it (sorted by the assembly's
 * category order), and any leftover parts returned as `unmatched`.
 */
export function buildJobAssemblyGroups(
  assemblies: IPartAssembly[] | undefined,
  jobPartCategories: JobPartCategory[]
): { groups: (IPartAssembly & { jobParts: JobPartCategory[] })[]; unmatched: JobPartCategory[] } {
  const matched = (assemblies ?? []).map((assembly) => {
    const categoryOrderMap = new Map(
      assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
    );

    const jobParts = jobPartCategories
      .filter((jobPart) =>
        assembly.partAssemblyPartCategories?.some(
          (cat) => cat.partCategoryId === jobPart.partCategoryId
        )
      )
      .sort((a, b) => {
        const orderA = categoryOrderMap.get(a.partCategoryId!) ?? MAX_ORDER;
        const orderB = categoryOrderMap.get(b.partCategoryId!) ?? MAX_ORDER;
        return orderA - orderB;
      });

    return { ...assembly, jobParts };
  });

  const unmatched = jobPartCategories.filter(
    (jobPart) =>
      !matched.some((assembly) =>
        assembly.partAssemblyPartCategories?.some(
          (cat) => cat.partCategoryId === jobPart.partCategoryId
        )
      )
  );

  return {
    groups: matched.filter((assembly) => assembly.jobParts.length > 0),
    unmatched,
  };
}

const esc = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const partName = (jobPart: JobPartCategory): string =>
  jobPart.partData?.category?.name ?? jobPart.partCategory?.name ?? "Part";

const positionLabel = (part: IPart | null): string => {
  if (!part) return "—";
  const parts = [
    part.side ? PartSide[part.side] : null,
    part.position ? PartPosition[part.position] : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "—";
};

const statusCell = (part: IPart | null): string => {
  if (part?.isDamaged) return '<span style="color:#b91c1c">Damaged</span>';
  if (part?.removedOn) return '<span style="color:#15803d">Removed</span>';
  return '<span style="color:#6b7280">Outstanding</span>';
};

const flowLabel = (part: IPart | null, accountNameById: Map<string, string>): string => {
  const accountId = part?.accountId;
  if (!accountId) return "—";
  return accountNameById.get(accountId) ?? "—";
};

const groupTable = (jobParts: JobPartCategory[], accountNameById: Map<string, string>): string => {
  if (jobParts.length === 0) {
    return '<p class="empty">No parts in this assembly.</p>';
  }

  const rows = jobParts
    .map(
      (jp) => `
        <tr>
          <td class="tick"></td>
          <td class="part">${esc(partName(jp))}</td>
          <td>${esc(jp.partData?.tagNumber || "—")}</td>
          <td>${esc(positionLabel(jp.partData ?? null))}</td>
          <td>${esc(flowLabel(jp.partData ?? null, accountNameById))}</td>
          <td>${statusCell(jp.partData ?? null)}</td>
        </tr>`
    )
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th class="tick">✓</th>
          <th>PART</th>
          <th>TAG</th>
          <th>POSITION</th>
          <th>FLOW</th>
          <th>STATUS</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
};

const groupSection = (group: DismantlingPlanGroup, accountNameById: Map<string, string>): string => `
  <section class="assembly">
    <h2>${esc(group.name)} <span class="count">(${group.jobParts.length})</span></h2>
    ${groupTable(group.jobParts, accountNameById)}
  </section>`;

export default function printDismantlingPlan(
  groups: DismantlingPlanGroup[],
  header: DismantlingPlanHeader = {},
  accounts: { id: string; name: string }[] = []
) {
  const nonEmpty = groups.filter((g) => g.jobParts.length > 0);
  if (nonEmpty.length === 0) return;

  const subtitleParts = [header.jobTitle, header.vrm, header.makeModel]
    .map((v) => (v ?? "").trim())
    .filter(Boolean);

  const accountNameById = new Map(accounts.map((account) => [account.id, account.name]));

  const html = `
    <html>
      <head>
        <title>Dismantling Plan</title>
        <style>
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
            margin: 24px;
          }
          h1 {
            font-size: 22px;
            font-weight: 700;
            color: #1e293b;
            margin: 0;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
            margin: 2px 0 20px;
          }
          .assembly {
            page-break-after: always;
          }
          .assembly:last-child {
            page-break-after: auto;
          }
          h2 {
            font-size: 16px;
            font-weight: 700;
            margin: 0 0 8px;
          }
          h2 .count {
            font-weight: 400;
            color: #9ca3af;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 5px 8px;
            text-align: left;
          }
          th {
            background-color: #f3f4f6;
            font-size: 11px;
            letter-spacing: 0.03em;
            color: #374151;
          }
          tr {
            page-break-inside: avoid;
          }
          th.tick, td.tick {
            width: 28px;
            text-align: center;
          }
          td.tick {
            height: 22px;
          }
          td.part {
            font-weight: 600;
          }
          .empty {
            font-size: 12px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <h1>Dismantling Plan</h1>
        ${subtitleParts.length > 0 ? `<div class="subtitle">${esc(subtitleParts.join(" · "))}</div>` : ""}
        ${nonEmpty.map((group) => groupSection(group, accountNameById)).join("")}
      </body>
    </html>`;

  printHtml(html);
}
